/**
 * Notification Context
 *
 * Provides toast notification functionality across the application.
 * Respects user preferences for notification types.
 */

'use client'

import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react'
import { useMondayAuth } from './MondayAuthContext'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
}

export interface NotificationPreferences {
  operationComplete: boolean
  operationFailed: boolean
  rollbackExpiring: boolean
  systemAlerts: boolean
}

export interface NotificationContextType {
  showToast: (message: string, type: ToastType, duration?: number) => void
  showSuccess: (message: string) => void
  showError: (message: string) => void
  showWarning: (message: string) => void
  showInfo: (message: string) => void
  preferences: NotificationPreferences | null
}

export const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
)

interface NotificationProviderProps {
  children: React.ReactNode
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  operationComplete: true,
  operationFailed: true,
  rollbackExpiring: false,
  systemAlerts: true
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null)
  const { organization } = useMondayAuth()

  // Load user notification preferences
  useEffect(() => {
    if (!organization) {
      setPreferences(DEFAULT_PREFERENCES)
      return
    }

    const loadPreferences = async () => {
      try {
        const response = await fetch(
          `/api/notifications/preferences?organizationId=${organization.id}`
        )

        if (response.ok) {
          const data = await response.json()
          setPreferences(data.settings || DEFAULT_PREFERENCES)
        } else {
          setPreferences(DEFAULT_PREFERENCES)
        }
      } catch (error) {
        console.error('[NotificationProvider] Failed to load preferences:', error)
        setPreferences(DEFAULT_PREFERENCES)
      }
    }

    loadPreferences()
  }, [organization])

  const showToast = useCallback(
    (message: string, type: ToastType, duration: number = 5000) => {
      const id = `toast-${Date.now()}-${Math.random()}`
      const newToast: Toast = { id, message, type, duration }

      setToasts((prev) => [...prev, newToast])

      // Auto-dismiss after duration
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, duration)
    },
    []
  )

  const showSuccess = useCallback(
    (message: string) => {
      if (preferences?.operationComplete !== false) {
        showToast(message, 'success')
      }
    },
    [preferences, showToast]
  )

  const showError = useCallback(
    (message: string) => {
      if (preferences?.operationFailed !== false) {
        showToast(message, 'error')
      }
    },
    [preferences, showToast]
  )

  const showWarning = useCallback(
    (message: string) => {
      if (preferences?.systemAlerts !== false) {
        showToast(message, 'warning')
      }
    },
    [preferences, showToast]
  )

  const showInfo = useCallback(
    (message: string) => {
      if (preferences?.systemAlerts !== false) {
        showToast(message, 'info')
      }
    },
    [preferences, showToast]
  )

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<NotificationContextType>(() => ({
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    preferences
  }), [showToast, showSuccess, showError, showWarning, showInfo, preferences])

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} />
    </NotificationContext.Provider>
  )
}

// Toast Container Component
function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md">
      {toasts.slice(-3).map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}

// Individual Toast Component
function ToastItem({ toast }: { toast: Toast }) {
  const bgColors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  }

  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  }

  return (
    <div
      className={`${bgColors[toast.type]} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in-right`}
    >
      <span className="text-xl font-bold">{icons[toast.type]}</span>
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
    </div>
  )
}
